import { SavedSelection } from './selectionUtils.types';

/**
 * 選択範囲を保存する関数
 * @param containerEl - リッチテキストのコンテナ要素
 * @returns 選択範囲の開始位置と終了位置、またはnull
 */
export const saveSelection = (containerEl: HTMLElement): SavedSelection | null => {
  if (!containerEl) return null;
  
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  
  try {
    const range = sel.getRangeAt(0);
    
    // 選択範囲が不正な場合はスキップ
    if (!containerEl.contains(range.startContainer) && !containerEl.contains(range.endContainer)) {
      return null;
    }
    
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(containerEl);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    const start = preSelectionRange.toString().length;
    
    return { 
      start, 
      end: start + range.toString().length,
      // 将来的な拡張のために追加情報を含める
      startContainer: range.startContainer,
      startOffset: range.startOffset,
      endContainer: range.endContainer,
      endOffset: range.endOffset
    };
  } catch (e) {
    console.error('選択範囲の保存に失敗しました:', e);
    return null;
  }
};

/**
 * 選択範囲を復元する関数
 * @param containerEl - リッチテキストのコンテナ要素
 * @param savedSel - 保存された選択範囲({start, end})
 */
export const restoreSelection = (containerEl: HTMLElement, savedSel: SavedSelection | null): void => {
  if (!containerEl || !savedSel) return;
  
  try {
    let charIndex = 0;
    let range = document.createRange();
    range.setStart(containerEl, 0);
    range.collapse(true);

    // 保存された参照が有効な場合は直接使用
    if (savedSel.startContainer && 
        savedSel.endContainer && 
        containerEl.contains(savedSel.startContainer) && 
        containerEl.contains(savedSel.endContainer)) {
      try {
        range.setStart(savedSel.startContainer, savedSel.startOffset || 0);
        range.setEnd(savedSel.endContainer, savedSel.endOffset || 0);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        return;
      } catch (e) {
        console.error('直接参照による選択範囲の復元に失敗しました:', e);
        // フォールバックとして通常の方法で復元を試みる
      }
    }

    const nodeStack: Node[] = [containerEl];
    const nodes: Node[] = [];
    while (nodeStack.length) {
      const node = nodeStack.pop();
      if (!node) continue;
      
      if (node.nodeType === Node.TEXT_NODE) {
        nodes.push(node);
      } else {
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
          nodeStack.push(node.childNodes[i]);
        }
      }
    }

    let startNode: Node | null = null;
    let startOffset = 0;
    let endNode: Node | null = null;
    let endOffset = 0;

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const nodeLength = node.textContent?.length || 0;
      const nextCharIndex = charIndex + nodeLength;
      
      if (!startNode && savedSel.start >= charIndex && savedSel.start <= nextCharIndex) {
        startNode = node;
        startOffset = savedSel.start - charIndex;
      }
      if (!endNode && savedSel.end >= charIndex && savedSel.end <= nextCharIndex) {
        endNode = node;
        endOffset = savedSel.end - charIndex;
        break;
      }
      charIndex = nextCharIndex;
    }

    if (startNode && endNode) {
      range.setStart(startNode, startOffset);
      range.setEnd(endNode, endOffset);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  } catch (e) {
    console.error('選択範囲の復元に失敗しました:', e);
  }
};

/**
 * 選択範囲の末尾にカーソルを移動する関数
 * @param containerEl - リッチテキストのコンテナ要素
 */
export const moveCaretToEnd = (containerEl: HTMLElement): void => {
  if (!containerEl) return;
  
  try {
    const range = document.createRange();
    range.selectNodeContents(containerEl);
    range.collapse(false); // 末尾に設定
    
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
      
      // 視覚的なフィードバックのためにスクロール
      containerEl.scrollTop = containerEl.scrollHeight;
    }
  } catch (e) {
    console.error('カーソル移動に失敗しました:', e);
  }
}; 